/**
 * Organizations API Schemas
 *
 * Pure Zod schemas for API request/response validation.
 * DB types come from @aica/database repositories.
 *
 * SINGLE SOURCE OF TRUTH for API contract.
 */

import { z } from 'zod/v4';
import { storedVendorNameSchema } from './common';
import { userRoleSchema } from './users';
import { deliveryHistoryItemSchema } from './settings';
import { notificationCategorySchema } from './notifications';

// ============================================================================
// Enums
// ============================================================================

/**
 * Provider type enum - defines the category of third-party service
 *
 * - `asr`: Automatic Speech Recognition (transcription) providers
 *   Valid names: elevenlabs
 *
 * - `llm`: Large Language Model (AI analysis) providers
 *   Valid names: openai
 *
 * - `email`: Email delivery providers
 *   Valid names: resend
 *
 * - `crm`: Customer Relationship Management providers
 *   Valid names: `crmProviderNameSchema`
 */
export const providerTypeSchema = z.enum(['asr', 'llm', 'email', 'crm']);

/**
 * The CRMs we ship an in-CRM app for.
 *
 * Spelled out rather than derived from the catalog, for a reason that is not
 * laziness: the catalog lives in @aica/core, and @aica/core depends on THIS
 * package — importing it back would close a cycle. The list is held to the
 * catalog by `embedded-vendor-enums.test.ts` in @aica/core, which can see both.
 */
export const crmProviderNameSchema = z.enum(['bitrix24', 'uspacy']);

/**
 * Provider name enum - specific service provider implementations
 *
 * ASR Providers (transcription):
 * - `elevenlabs`: ElevenLabs Scribe v2 ASR with multi-language support
 *   (removed 2026-09-11: `deepgram` — ElevenLabs is the only ASR sub-processor)
 *
 * LLM Providers (AI analysis):
 * - `openai`: OpenAI GPT models (GPT-5-mini, GPT-4o, etc.)
 *
 * Email Providers:
 * - `resend`: Resend email delivery service
 *
 * CRM Providers: the CRMs of `crmProviderNameSchema` — taken from it rather
 * than named again here, so the one list held to the catalog is the only list.
 */
export const providerNameSchema = z.enum([
  'elevenlabs',
  'openai',
  'resend',
  ...crmProviderNameSchema.options,
]);

// ============================================================================
// Path Parameter Schemas
// ============================================================================

// NOTE: Use orgIdParamSchema from common.ts for organization ID params

/**
 * Path parameter for provider credential lookup
 */
export const providerCredentialParamSchema = z.object({
  orgId: z.string().uuid(),
  providerType: providerTypeSchema,
  providerName: z.string().min(1).max(50),
});

// ============================================================================
// Query Schemas
// ============================================================================

/**
 * Query schema for organization metrics
 */
export const organizationMetricsQuerySchema = z.object({
  range: z.enum(['7d', '30d', '90d', 'all']).optional().default('30d'),
});

// ============================================================================
// Organization Response Schemas
// ============================================================================

/**
 * Organization response schema - matches GET /api/v1/organizations/:id
 *
 * Contains organization metadata, billing configuration, and regional settings.
 *
 * Key fields:
 * - `billingAccountId`: Links to billing account for usage tracking and invoicing
 * - `region`: Data region — single EU deployment, always 'eu'
 * - `bitrix24Domain`: Custom Bitrix24 domain for CRM integration (e.g., 'company.bitrix24.com')
 * - `settings`: Flexible JSON object for org-specific configuration (e.g., default project settings, feature flags)
 */
export const organizationResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  billingAccountId: z.string().nullable(),
  region: z.string().nullable(),
  bitrix24Domain: z.string().nullable(),
  settings: z.unknown().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const getOrganizationResponseSchema = z.object({
  organization: organizationResponseSchema,
});

export const updateOrganizationRequestSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    bitrix24Domain: z.string().optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

// ============================================================================
// Provider Credentials Schemas
// ============================================================================

/**
 * Provider credential response schema - used in provider credentials API endpoints
 * - GET /api/v1/organizations/:orgId/providers/credentials
 * - GET /api/v1/organizations/:orgId/providers/credentials/:providerType/:providerName
 * - POST /api/v1/organizations/:orgId/providers/credentials (after creation)
 *
 * Contains metadata about configured third-party provider credentials.
 *
 * Key fields:
 * - `providerType`: Category of service (asr, llm, email, crm)
 * - `providerName`: Specific provider implementation (elevenlabs, openai, resend, etc.)
 * - `isActive`: Whether this credential is currently used for API calls
 *
 * SECURITY: This schema intentionally excludes the actual credential data (API keys, secrets, etc.)
 * for security reasons. Credentials are only accepted during creation/update and are never returned
 * in API responses. They are stored encrypted in the database and only decrypted when making
 * provider API calls.
 *
 * The field set mirrors the route's actual masked response (`toPublicCredential`)
 * and `settings.providerCredentialSchema`. `orgId` is intentionally omitted —
 * the client already has it from the URL path and the route never returns it
 * (audit P0-2 / L6 — keeps the two canonical masked schemas in agreement).
 */
// Responses must tolerate a row naming a provider we have since retired
// ('openrouter', removed 2026-08-11) — a single stale row must not void the
// whole list response client-side. Generic, so retiring the next vendor needs
// no edit here; requests keep the strict `providerNameSchema`.
export const storedProviderNameSchema = storedVendorNameSchema<ProviderName>();

export const providerCredentialResponseSchema = z.object({
  id: z.string(),
  providerType: providerTypeSchema,
  providerName: storedProviderNameSchema,
  isActive: z.boolean(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const listProviderCredentialsResponseSchema = z.object({
  credentials: z.array(providerCredentialResponseSchema),
  total: z.number().int(),
});

export const createProviderCredentialRequestSchema = z
  .object({
    providerType: providerTypeSchema,
    providerName: providerNameSchema,
    credentials: z.record(z.string(), z.string()), // { apiKey: "...", clientId: "...", etc }
  })
  .strict();

export const updateProviderCredentialRequestSchema = z
  .object({
    credentials: z.record(z.string(), z.string()).optional(),
    isActive: z.boolean().optional(),
  })
  .strict();

/**
 * Create provider credential input
 */
export const createProviderCredentialInputSchema = z.object({
  providerType: providerTypeSchema,
  providerName: z.string().min(1).max(50),
  credentialsJson: z.string().min(1), // JSON string - structure validated separately
  isActive: z.boolean().optional().default(true),
});

/**
 * Update provider credential input
 */
export const updateProviderCredentialInputSchema = z.object({
  credentialsJson: z.string().min(1).optional(),
  isActive: z.boolean().optional(),
});

export const createProviderCredentialResponseSchema = z.object({
  credential: providerCredentialResponseSchema,
  message: z.string().optional(),
});

// ============================================================================
// Organization Metrics Schemas
// ============================================================================

export const organizationMetricsResponseSchema = z.object({
  metrics: z.object({
    totalProjects: z.number().int(),
    totalRecords: z.number().int(),
    totalUsers: z.number().int(),
    storageUsedBytes: z.number(),
    monthlyUsage: z.object({
      transcriptionMinutes: z.number(),
      analysisTokens: z.number().int(),
      costCents: z.number().int(),
    }),
  }),
});

// ============================================================================
// Delivery Settings Schemas
// ============================================================================

export const deliverySettingsSchema = z.object({
  emailEnabled: z.boolean(),
  emailRecipients: z.array(z.string().email()),
  webhookEnabled: z.boolean(),
  webhookUrl: z.string().url().nullable(),
  webhookSecret: z.string().nullable(),
  crmEnabled: z.boolean(),
  crmFieldMappings: z.record(z.string(), z.string()).nullable(),
});

export const getDeliverySettingsResponseSchema = z.object({
  settings: deliverySettingsSchema,
});

export const updateDeliverySettingsRequestSchema = deliverySettingsSchema.partial();

/**
 * CRM delivery configuration schema (embedded settings)
 * Used for POST/PATCH /organizations/:org_id/delivery/config
 *
 * LEGACY: This schema is for backward compatibility with old delivery_config structure.
 * New code should use crmProviderConfigSchema with multi-provider support.
 */
export const deliveryConfigSchema = z.object({
  deliverToCrmCard: z.boolean().default(false),
  deliverToSmartProcess: z.boolean().default(false),
  smartProcessId: z.string().optional().nullable(),
  fields: z.object({
    transcript: z.boolean(),
    summary: z.boolean(),
    qualityScore: z.boolean(),
    saleProbability: z.boolean(),
    keywords: z.boolean(),
    sentiment: z.boolean(),
  }),
});

/**
 * CRM provider configuration schema (multi-CRM support)
 *
 * Each provider has its own field_mappings and configuration.
 */
export const crmProviderConfigSchema = z.object({
  enabled: z.boolean(),
  field_mappings: z.record(z.string(), z.string()), // { transcript: "UF_CRM_123", summary: "UF_CRM_456" }
  call_series_field_mappings: z
    .record(
      z.string(), // call number as string ("1", "2", etc.)
      z.record(z.string(), z.string()) // { fieldName: "UF_CRM_FIELD" }
    )
    .optional(),
  config: z.object({
    destination: z.enum(['crm_card', 'smart_process']).optional(),
    smartProcessId: z.string().optional().nullable(),
    crmCardEnabled: z.boolean().optional(),
    smartProcessEnabled: z.boolean().optional(),
    /** Number of UF_ fields created in Bitrix24 Smart Process (0 = fields missing) */
    spFieldsCreated: z.number().int().optional(),
  }),
});

/**
 * Organization CRM providers schema (multi-CRM support)
 */
export const orgCrmProvidersSchema = z.object({
  bitrix24: crmProviderConfigSchema.optional(),
  uspacy: crmProviderConfigSchema.optional(),
});

/**
 * A provider in the body of `PATCH …/delivery/config`: every part optional —
 * the server leaves what is absent as stored. Each writer sends only its own
 * part (the field table `field_mappings`, the destination cards `config`), not
 * a copy of the provider from its cache (#2987). Only for the PATCH body: GET
 * and the stored shape keep the full `crmProviderConfigSchema`.
 */
export const crmProviderConfigPatchSchema = z.object({
  enabled: z.boolean().optional(),
  /**
   * Merged key by key (#3001): a key absent from the body stays as stored, so
   * two writers editing different results never revert each other. `null` is
   * the explicit «Поле Aelo по умолчанию» marker — the stored key is removed
   * and delivery falls back to `UF_CRM_AICA_…`.
   */
  field_mappings: z.record(z.string(), z.string().nullable()).optional(),
  config: crmProviderConfigSchema.shape.config.optional(),
});

export const orgCrmProvidersPatchSchema = z.object({
  bitrix24: crmProviderConfigPatchSchema.optional(),
  uspacy: crmProviderConfigPatchSchema.optional(),
});

/**
 * Generic field configuration schema (multi-CRM support)
 *
 * Defines which fields are enabled regardless of CRM provider.
 * Includes all available analysis output parameters (29 total: 26 data fields + 3 special actions).
 */
export const genericFieldConfigSchema = z.object({
  // ============================================================================
  // Basic Fields (6)
  // ============================================================================
  transcript: z.boolean(),
  summary: z.boolean(),
  qualityScore: z.boolean(),
  saleProbability: z.boolean(),
  keywords: z.boolean(),
  sentiment: z.boolean(),

  // ============================================================================
  // Quality Score Breakdown (5)
  // ============================================================================
  dialogueTechnique: z.boolean(),
  contentQuality: z.boolean(),
  emotionalIntelligence: z.boolean(),
  complianceScore: z.boolean(),
  speechQuality: z.boolean(),

  // ============================================================================
  // Dialogue Metrics (3)
  // ============================================================================
  needsQualification: z.boolean(), // BANT scores
  objectionHandling: z.boolean(),
  closingTechnique: z.boolean(),

  // ============================================================================
  // Content Metrics (3)
  // ============================================================================
  relevance: z.boolean(),
  completeness: z.boolean(),
  accuracy: z.boolean(),

  // ============================================================================
  // Emotional Metrics (3)
  // ============================================================================
  empathy: z.boolean(),
  rapport: z.boolean(),
  sentimentDelta: z.boolean(),

  // ============================================================================
  // Compliance Metrics (3)
  // ============================================================================
  recordingDisclaimer: z.boolean(),
  tcpaCompliance: z.boolean(),
  dataPrivacy: z.boolean(),

  // ============================================================================
  // Speech Metrics (3)
  // ============================================================================
  clarity: z.boolean(),
  professionalism: z.boolean(),
  fillerWords: z.boolean(),

  // ============================================================================
  // Special Actions (3) - Bitrix24-specific
  // ============================================================================
  /** Attach transcription to call via telephony.call.attachTranscription API */
  attachTranscription: z.boolean(),
  /** Add formatted analysis report as timeline comment in deal/lead card */
  addTimelineComment: z.boolean(),
  /** Save formatted analysis report as timeline note on the call or open-line chat activity via crm.timeline.note.save */
  addTimelineNote: z.boolean(),

  // ============================================================================
  // CRM Delivery Enrichment (2) — opt-in, default off for backward compat
  // ============================================================================
  /** Auto-fill empty CRM fields with values extracted from the transcript */
  autoFillEmptyFields: z.boolean().default(false),
  /** Create CRM Activities (planned call/task) from extracted call agreements */
  createActivitiesFromAgreements: z.boolean().default(false),
});

type GenericFieldConfigKey = keyof typeof genericFieldConfigSchema.shape;

/**
 * `genericFieldConfig` in the body of `PATCH …/delivery/config`: only the flags
 * a writer changed (#3001). The server merges them key by key over what is
 * stored, so a writer with a stale cache never reverts another writer's flag.
 * Built by hand, not `.partial()`: Zod 4 still fills a `.default()` inside a
 * partial, and an absent `autoFillEmptyFields` would come back as `false` —
 * switching it off for everyone who saved anything else.
 */
export const genericFieldConfigPatchSchema = z.object(
  Object.fromEntries(
    Object.keys(genericFieldConfigSchema.shape).map((key) => [
      key,
      z.boolean().optional(),
    ])
  ) as Record<GenericFieldConfigKey, z.ZodOptional<z.ZodBoolean>>
);

/**
 * Operator-selected CRM fields eligible for auto-fill, per entity type.
 * Values are CRM field IDs (e.g. ['UF_CRM_123','COMMENTS']).
 */
export const autofillFieldsSchema = z.object({
  deal: z.array(z.string()).default([]),
  lead: z.array(z.string()).default([]),
  contact: z.array(z.string()).default([]),
  company: z.array(z.string()).default([]),
});

/**
 * «Комментарий о состоянии сделки» в таймлайне CRM (#766, PR3, решение
 * владельца V5) — свой выключатель организации в настройках CRM, по умолчанию
 * включён (R7). Не зависит от `addTimelineComment`/`addTimelineNote` отчёта
 * по звонку.
 *
 * Хранится отдельным ключом `orgs.settings_json.deal_state_comment`, а не в
 * `generic_field_config`: PATCH настроек доставки заменяет
 * `generic_field_config` целиком, и любой клиент, который не знает о ключе
 * (старая вкладка интерфейса, откат edge-api, чья схема молча выбросит
 * незнакомое поле), сбросил бы выключенный комментарий обратно во «включён».
 * Отдельный ключ меняется только телом, где он есть; проверка «хоть одно поле
 * отчёта включено» его не видит. Читают edge-api (GET/PATCH
 * `/api/v1/organizations/:orgId/delivery/config`, поле `dealStateComment`) и
 * workflows-api (публикация комментария) — оба через
 * `isDealStateCommentEnabled`.
 */
export const DEAL_STATE_COMMENT_SETTING_KEY = 'deal_state_comment';
export const dealStateCommentSettingSchema = z.boolean();

/** Включён ли комментарий у организации: ключа нет или он не булев — включён. */
export function isDealStateCommentEnabled(
  settings: Readonly<Record<string, unknown>> | null | undefined
): boolean {
  const parsed = dealStateCommentSettingSchema.safeParse(
    settings?.[DEAL_STATE_COMMENT_SETTING_KEY]
  );
  return parsed.success ? parsed.data : true;
}

/**
 * Активная CRM организации — так её читает доставка отчёта
 * (`active_crm_provider`; нет или не разбирается — `bitrix24`).
 */
export function resolveActiveCrmProvider(raw: unknown): CrmProviderName {
  const parsed = crmProviderNameSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'bitrix24';
}

/**
 * В какую CRM организация пишет отчёт и комментарий о состоянии сделки:
 * активная CRM, если она включена в `crm_providers`; иначе — никуда (`null`).
 * Одно правило на троих — доставка отчёта и комментарий в workflows-api,
 * экран переключателя в edge-api (`dealStateCommentVendor`): переключатель,
 * показанный «включённым» там, куда комментарий не уходит, обещал бы то, чего
 * нет (ревью PR #1613).
 */
export function deliveryCrmVendorOf(
  settings: Readonly<Record<string, unknown>> | null | undefined
): CrmProviderName | null {
  const active = resolveActiveCrmProvider(settings?.active_crm_provider);
  const providers = orgCrmProvidersSchema.safeParse(settings?.crm_providers);
  return providers.success && providers.data[active]?.enabled === true ? active : null;
}

/**
 * Multi-provider delivery configuration response schema
 *
 * Returned by GET /api/v1/settings/delivery/config
 */
export const multiProviderDeliveryConfigResponseSchema = z.object({
  activeCrmProvider: crmProviderNameSchema,
  crmProviders: orgCrmProvidersSchema,
  genericFieldConfig: genericFieldConfigSchema,
  autofillFields: autofillFieldsSchema.optional(),
  /**
   * Комментарий о состоянии сделки (V5). Необязателен: edge-api до PR3 его не
   * возвращает — отсутствие значит «включён» (`isDealStateCommentEnabled`).
   */
  dealStateComment: dealStateCommentSettingSchema.optional(),
  /**
   * CRM, в которую уходит комментарий (`deliveryCrmVendorOf`); `null` — никуда.
   * Необязателен: edge-api до этого поля его не возвращает.
   */
  dealStateCommentVendor: crmProviderNameSchema.nullable().optional(),
});

/**
 * The same GET for an organization with no multi-provider settings yet
 * (`configured: false`, CP-003): there is no provider to configure, so the
 * multi-provider reader answers `null` instead of failing on the nulls
 * `orgDeliveryConfigResponseSchema` allows.
 */
export const unconfiguredDeliveryConfigResponseSchema = z.object({
  configured: z.literal(false),
});

/**
 * What GET /api/v1/organizations/:orgId/delivery/config returns — the shape the
 * handler builds, not the project-level one an older schema described.
 *
 * `configured: false` means the organization has no multi-provider settings
 * yet: `activeCrmProvider` and `genericFieldConfig` are then `null` and
 * `crmProviders` is `{}` (no synthesized defaults, CP-003).
 */
export const orgDeliveryConfigResponseSchema = z.object({
  configured: z.boolean(),
  activeCrmProvider: crmProviderNameSchema.nullable(),
  crmProviders: orgCrmProvidersSchema,
  genericFieldConfig: genericFieldConfigSchema.nullable(),
  autofillFields: autofillFieldsSchema,
  dealStateComment: dealStateCommentSettingSchema,
  dealStateCommentVendor: crmProviderNameSchema.nullable(),
  /** The ten most recent deliveries of the organization. */
  recentDeliveries: z.array(deliveryHistoryItemSchema),
});

/**
 * What PATCH /api/v1/organizations/:orgId/delivery/config answers: the settings
 * as stored after the server merged the body into them key by key — what another
 * writer saved meanwhile included (#3025) — without the deliveries the GET adds.
 */
export const orgDeliveryConfigPatchResponseSchema = orgDeliveryConfigResponseSchema.omit({
  recentDeliveries: true,
});

// ============================================================================
// Consent Configuration Schemas (Compliance)
// ============================================================================

/**
 * How the organization handles recording notice/consent operationally.
 *
 * `other_lawful_basis` covers organizations that do not rely on participant
 * consent and instead select the applicable basis separately in `legalBasis`.
 * The field name remains `consentMode` for backwards API compatibility.
 */
export const consentModeSchema = z.enum([
  'all_party',
  'one_party',
  'employer_notification',
  'other_lawful_basis',
]);

export const consentLegalBasisSchema = z.enum([
  'consent',
  'legitimate_interest',
  'contract',
  'legal_obligation',
  'other_applicable_law',
]);

/**
 * Consent configuration for organization
 * Stored in orgs.settings_json — no migration needed
 */
export const consentConfigSchema = z.object({
  consentRequired: z.boolean().default(true),
  consentMode: consentModeSchema.default('all_party'),
  jurisdiction: z.string().default(''),
  legalBasis: consentLegalBasisSchema.default('consent'),
  consentConfirmedBy: z.string().nullable().default(null),
  consentConfirmedAt: z.string().nullable().default(null),
});

// ============================================================================
// Processing Toggle (explicit start of analysis)
// ============================================================================

/**
 * Флаг «анализ включён», хранится в orgs.settings_json — миграция не нужна.
 *
 * Дефолт true: организация без поля работает как раньше. Явный false ставится
 * только при создании организации, чтобы новый клиент успел осмотреться,
 * прежде чем поток звонков начнёт тратить включённые минуты. Читать флаг в
 * рантайме следует через `isProcessingEnabled` из @aica/core — он переживает
 * битый settings_json, чего разбор этой схемой не гарантирует.
 */
export const processingConfigSchema = z.object({
  processingEnabled: z.boolean().default(true),
});

// ============================================================================
// Notification Defaults Schemas
// ============================================================================

/**
 * orgs.settings_json.notificationDefaults — per-role subscription defaults,
 * set by an org admin/owner (PATCH /api/v1/settings, `organization.update`
 * permission). Read at delivery time by `resolveAlertRecipients`
 * (packages/database/src/services/alert-recipients.ts) and inherited by any
 * member whose own `categoryFilter` is `null` (`resolveAlertCategoryEnabled`,
 * UserNotificationChannelsRepository.ts).
 *
 * Both levels are PARTIAL, not a full record: an org need not set every
 * role, and a role need not set every category. A missing entry means "no
 * default recorded" — which `resolveAlertCategoryEnabled` resolves to OFF —
 * not "explicitly disabled". Reuses `userRoleSchema` (the org-role SSOT) and
 * `notificationCategorySchema` (the category SSOT) so this can never drift
 * from either enum.
 *
 * The route handler additionally rejects a role/category pair that
 * `getAllowedNotificationCategories` (`@aica/core`) would never grant that
 * role — a role/category combination this schema accepts on its own is not
 * automatically a combination the API accepts (mirrors how
 * `updateUserNotificationChannelsRequestSchema.categoryFilter` is validated
 * against the caller's own allowed categories in
 * routes/settings/notification-channels.ts).
 */
export const notificationDefaultsSchema = z.partialRecord(
  userRoleSchema,
  z.partialRecord(notificationCategorySchema, z.boolean())
);

export type NotificationDefaults = z.infer<typeof notificationDefaultsSchema>;

// ============================================================================
// Webhook Settings Schemas
// ============================================================================

/**
 * Organization webhook settings
 * Stored in orgs.settings_json
 */
export const webhookSettingsSchema = z.object({
  webhookProcessingDelay: z.number().int().min(0).max(30).default(0),
});

export type WebhookSettings = z.infer<typeof webhookSettingsSchema>;

/**
 * Call Data Smart Process settings
 * Stored in orgs.settings_json
 */
export const callDataSmartProcessSettingsSchema = z.object({
  callDataSmartProcessEnabled: z.boolean().default(false),
  callDataSmartProcessId: z.string().nullable().optional(),
});

export type CallDataSmartProcessSettings = z.infer<
  typeof callDataSmartProcessSettingsSchema
>;

// ============================================================================
// Type exports (inferred from schemas)
// ============================================================================

export type ProviderType = z.infer<typeof providerTypeSchema>;
export type ProviderName = z.infer<typeof providerNameSchema>;
// OrgIdParam - use from common.ts
export type ProviderCredentialParam = z.infer<typeof providerCredentialParamSchema>;
export type OrganizationMetricsQuery = z.infer<typeof organizationMetricsQuerySchema>;

export type OrganizationResponse = z.infer<typeof organizationResponseSchema>;
export type UpdateOrganizationRequest = z.infer<typeof updateOrganizationRequestSchema>;

export type ProviderCredentialResponse = z.infer<typeof providerCredentialResponseSchema>;
export type CreateProviderCredentialRequest = z.infer<
  typeof createProviderCredentialRequestSchema
>;
export type UpdateProviderCredentialRequest = z.infer<
  typeof updateProviderCredentialRequestSchema
>;
export type CreateProviderCredentialInput = z.infer<
  typeof createProviderCredentialInputSchema
>;
export type UpdateProviderCredentialInput = z.infer<
  typeof updateProviderCredentialInputSchema
>;

export type DeliverySettings = z.infer<typeof deliverySettingsSchema>;
export type UpdateDeliverySettingsRequest = z.infer<
  typeof updateDeliverySettingsRequestSchema
>;
export type DeliveryConfig = z.infer<typeof deliveryConfigSchema>;

// Multi-provider types
export type CrmProviderName = z.infer<typeof crmProviderNameSchema>;
export type CrmProviderConfig = z.infer<typeof crmProviderConfigSchema>;
export type OrgCrmProviders = z.infer<typeof orgCrmProvidersSchema>;
export type GenericFieldConfig = z.infer<typeof genericFieldConfigSchema>;
export type GenericFieldConfigPatch = z.infer<typeof genericFieldConfigPatchSchema>;
export type OrgCrmProvidersPatch = z.infer<typeof orgCrmProvidersPatchSchema>;
export type AutofillFields = z.infer<typeof autofillFieldsSchema>;
export type MultiProviderDeliveryConfigResponse = z.infer<
  typeof multiProviderDeliveryConfigResponseSchema
>;
export type OrgDeliveryConfigResponse = z.infer<typeof orgDeliveryConfigResponseSchema>;
export type OrgDeliveryConfigPatchResponse = z.infer<
  typeof orgDeliveryConfigPatchResponseSchema
>;

// Consent config type
export type ConsentConfig = z.infer<typeof consentConfigSchema>;

// Processing toggle type
export type ProcessingConfig = z.infer<typeof processingConfigSchema>;
